import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY_INFO = {
  name: "HEALTHCENTRIX LLC",
  phone: "844-943-6175",
  address: "2626 Glenwood Ave Ste 310, Raleigh, NC 27608"
};

const faqs = [
  {
    question: "Do I need an appointment?",
    answer: "While walk-ins are welcome for urgent care, we recommend scheduling an appointment for shorter wait times."
  },
  {
    question: "What insurance do you accept?",
    answer: "We accept most major insurance plans including Medicare, Medicaid, Blue Cross, Aetna, and UnitedHealthcare."
  },
  {
    question: "How can I access my medical records?",
    answer: "You can request your medical records through our patient portal or by contacting our records department."
  },
  {
    question: "Do you offer telemedicine appointments?",
    answer: "Yes, we offer virtual consultations for certain conditions. Please call to schedule a telemedicine appointment."
  },
];

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Get in Touch</span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mt-4 mb-6">
              Contact <span className="text-gradient">Us</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have questions? We're here to help. Reach out to us and we'll respond as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                icon: MapPin,
                label: "Main Office",
                value: "2626 Glenwood Ave, Ste 310\nRaleigh, NC 27608",
                color: "#00f0ff",
              },
              {
                icon: MapPin,
                label: "Headquarters",
                value: "6631 E 2nd St, Ste 100\nCasper, WY 82609",
                color: "#ff00ff",
              },
              {
                icon: Phone,
                label: "Call Us",
                value: "844-943-6175",
                href: "tel:8449436175",
                color: "#ff9e00",
              },
              {
                icon: Mail,
                label: "Email Us",
                value: "info@healthcentrixllc.online",
                href: "mailto:info@healthcentrixllc.online",
                color: "#00f0ff",
              },
              {
                icon: Clock,
                label: "Hours",
                value: "Mon-Fri: 9AM-5PM\nSat-Sun: 11AM-4PM",
                color: "#ff00ff",
              },
            ].map((item, index) => (
              <div
                key={index}
                data-testid={`contact-info-${index}`}
                className="contact-card"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${item.color}15` }}
                >
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">{item.label}</h4>
                  {item.href ? (
                    <a 
                      href={item.href}
                      className="text-gray-400 text-sm hover:text-[#00f0ff] transition-colors whitespace-pre-line"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-gray-400 text-sm whitespace-pre-line">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="glass rounded-3xl p-8 lg:p-10">
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-input-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-gray-300 mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-input-email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-gray-300 mb-2 block">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="contact-input-phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-gray-300 mb-2 block">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    data-testid="contact-input-subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="How can we help?"
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300 mb-2 block">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-input-message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Your message..."
                    rows={5}
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500 focus:border-[#00f0ff]"
                  />
                </div>

                <Button 
                  type="submit"
                  data-testid="contact-submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all disabled:opacity-50"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                  <Send className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </div>

            {/* Map & Hours */}
            <div className="space-y-8">
              {/* Map Placeholder */}
              <div className="glass rounded-3xl overflow-hidden h-80">
                <iframe
                  title="HealthCentrix Main Office"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3232.5!2d-78.6605!3d35.8137!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0!2s2626%20Glenwood%20Ave%2C%20Raleigh%2C%20NC%2027608!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="grayscale contrast-125 opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-500"
                />
              </div>

              {/* Office Hours */}
              <div className="glass rounded-3xl p-8">
                <h3 className="text-xl font-bold text-white mb-6">Office Hours</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Monday - Friday</span>
                    <span className="text-white">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saturday - Sunday</span>
                    <span className="text-white">11:00 AM - 4:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div className="glass rounded-3xl p-8 border border-[#ff00ff]/30">
                <h3 className="text-xl font-bold text-white mb-4">Our Locations</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-[#00f0ff] font-semibold text-sm uppercase tracking-wider mb-1">Main Office</p>
                    <p className="text-gray-300">2626 Glenwood Ave, Ste 310</p>
                    <p className="text-gray-300">Raleigh, NC 27608</p>
                  </div>
                  <div className="h-px bg-[#4b0082]" />
                  <div>
                    <p className="text-[#ff00ff] font-semibold text-sm uppercase tracking-wider mb-1">Headquarters</p>
                    <p className="text-gray-300">6631 E 2nd St, Ste 100</p>
                    <p className="text-gray-300">Casper, WY 82609</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">FAQ</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-item-${index}`}
                className="faq-item"
              >
                <button
                  className="faq-question w-full text-left"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="text-white font-medium">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-[#00f0ff]" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="faq-answer animate-fade-in-up">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
